import { computed, type ComputedRef } from 'vue'
import { usePermissionsStore } from '@/stores'
import { storeToRefs } from 'pinia'

export interface UsePermissionsReturn {
  roles: ComputedRef<string[]>
  permissions: ComputedRef<string[]>
  isAdmin: ComputedRef<boolean>
  isSuperAdmin: ComputedRef<boolean>
  delegations: ComputedRef<import('@/types/rbac').Delegation[]>
  manageableDomains: ComputedRef<import('@/types/rbac').ManageableDomain[]>
  can: (permission: string) => ComputedRef<boolean>
  canAny: (permissions: string[]) => ComputedRef<boolean>
  canAll: (permissions: string[]) => ComputedRef<boolean>
  canOnDomain: (domainId: number, permission: string) => ComputedRef<boolean>
  canManageDomain: (domainId: number) => ComputedRef<boolean>
  isOwner: (domainId: number) => ComputedRef<boolean>
  getDomainPermissions: (domainId: number) => ComputedRef<string[]>
}

export function usePermissions(): UsePermissionsReturn {
  const permissionsStore = usePermissionsStore()
  const storeRefs = storeToRefs(permissionsStore)

  // Wrap store refs in computed to ensure ComputedRef type
  const roles = computed(() => storeRefs.roles.value)
  const permissions = computed(() => storeRefs.permissions.value)
  const isAdmin = computed(() => storeRefs.isAdmin.value)
  const isSuperAdmin = computed(() => storeRefs.isSuperAdmin.value)
  const delegations = computed(() => storeRefs.delegations.value)
  const manageableDomains = computed(() => storeRefs.manageableDomains.value)

  const can = (permission: string): ComputedRef<boolean> => {
    return computed(() => permissionsStore.hasPermission(permission))
  }

  const canAny = (perms: string[]): ComputedRef<boolean> => {
    return computed(() => permissionsStore.hasAnyPermission(perms))
  }

  const canAll = (perms: string[]): ComputedRef<boolean> => {
    return computed(() => permissionsStore.hasAllPermissions(perms))
  }

  const canOnDomain = (domainId: number, permission: string): ComputedRef<boolean> => {
    return computed(() => permissionsStore.hasDomainPermission(domainId, permission))
  }

  const canManageDomain = (domainId: number): ComputedRef<boolean> => {
    return computed(() => permissionsStore.canManageDomain(domainId))
  }

  const isOwnerFn = (domainId: number): ComputedRef<boolean> => {
    return computed(() => permissionsStore.isOwner(domainId))
  }

  const getDomainPermissionsFn = (domainId: number): ComputedRef<string[]> => {
    return computed(() => permissionsStore.getDomainPermissions(domainId))
  }

  return {
    roles,
    permissions,
    isAdmin,
    isSuperAdmin,
    delegations,
    manageableDomains,
    can,
    canAny,
    canAll,
    canOnDomain,
    canManageDomain,
    isOwner: isOwnerFn,
    getDomainPermissions: getDomainPermissionsFn
  }
}
